import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Card, Table, Button, Form, Modal, 
  Spinner, Badge, Pagination, Alert
} from 'react-bootstrap';
import { useUsers } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const {
    users,
    loading,
    pagination,
    filters,
    getUsers,
    createUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    setFilters,
    clearCurrentUser
  } = useUsers();

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create'); // 'create' | 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [validated, setValidated] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    phone: '',
    address: '',
    permissions: {}
  });

  useEffect(() => {
    console.log("📄 UserManagement: useEffect llamado. Filtros:", filters);
    loadUsers();
  }, [filters, getUsers]);

  const loadUsers = async (page = 1) => {
    console.log("🔄 Cargando usuarios con página:", page);
    await getUsers(page, 10);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ [name]: value });
  };

  const clearFilters = () => {
    setFilters({ search: '', role: '', isActive: '' });
  };

  const openCreateModal = () => {
    setModalType('create');
    setFormData({
      name: '',
      email: '',
      password: '',
      role: '',
      phone: '',
      address: ''
    });
    setValidated(false);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setModalType('edit');
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      phone: user.phone || '',
      address: user.address || ''
    });
    setValidated(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    clearCurrentUser();
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setValidated(true);

    try {
      let result;
      if (modalType === 'create') {
        result = await createUser(formData);
      } else {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // No actualizar contraseña si está vacía
        }
        result = await updateUser(selectedUser._id, updateData);
      }

      if (result.success) {
        closeModal();
        loadUsers(pagination.currentPage);
      }
    } catch (error) {
      console.error('Error en formulario:', error);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (userToDelete) {
      const result = await deleteUser(userToDelete._id);
      if (result.success) {
        setShowDeleteModal(false);
        setUserToDelete(null);
        loadUsers(pagination.currentPage);
      }
    }
  };

  const handleToggleStatus = async (user) => {
    const result = await toggleUserStatus(user._id);
    if (result.success) {
      loadUsers(pagination.currentPage);
    }
  };

  const handlePageChange = (page) => {
    loadUsers(page);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const validatePassword = (password) => {
    if (modalType === 'edit' && !password) return true; // Contraseña opcional en edición
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
    return regex.test(password) && password.length >= 6;
  };

  return (
    <Container className="mt-4">
      <div className="fade-in">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body className="text-center py-4">
                <h1 className="text-gradient mb-3">
                  <i className="fas fa-users-cog me-3"></i>
                  Gestión de Usuarios
                </h1>
                <p className="lead text-muted">
                  Administra los usuarios del sistema COPFAMI
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filtros y acciones */}
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <Row className="align-items-end">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="form-label-custom">
                        <i className="fas fa-search me-2"></i>
                        Buscar
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="search"
                        value={filters.search}
                        onChange={handleFilterChange}
                        placeholder="Nombre o email..."
                        className="form-control-custom"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label className="form-label-custom">
                        <i className="fas fa-user-tag me-2"></i>
                        Rol
                      </Form.Label>
                      <Form.Select
                        name="role"
                        value={filters.role}
                        onChange={handleFilterChange}
                        className="form-control-custom"
                      >
                        <option value="">Todos</option>
                        <option value="super-admin">Super Administrador</option>
                        <option value="admin">Administrador</option>
                        <option value="secretaria">Secretaria</option>
                        <option value="socio">Socio</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label className="form-label-custom">
                        <i className="fas fa-toggle-on me-2"></i>
                        Estado
                      </Form.Label>
                      <Form.Select
                        name="isActive"
                        value={filters.isActive}
                        onChange={handleFilterChange}
                        className="form-control-custom"
                      >
                        <option value="">Todos</option>
                        <option value="true">Activos</option>
                        <option value="false">Inactivos</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-secondary"
                        onClick={clearFilters}
                        className="flex-fill"
                      >
                        <i className="fas fa-times me-2"></i>
                        Limpiar
                      </Button>
                      <Button
                        className="btn-primary-custom flex-fill"
                        onClick={openCreateModal}
                      >
                        <i className="fas fa-plus me-2"></i>
                        Nuevo Usuario
                      </Button>
                    </div>
                  </Col>

                  <Col md={2}>
                    <div className="text-muted small">
                      <i className="fas fa-info-circle me-1"></i>
                      {pagination.total} usuario{pagination.total !== 1 ? 's' : ''} total{pagination.total !== 1 ? 'es' : ''}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabla de usuarios */}
        <Row>
          <Col>
            <Card className="card-custom">
              <Card.Header className="card-header-custom">
                <h5 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  Lista de Usuarios
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                {loading ? (
                  <div className="loading-spinner">
                    <Spinner animation="border" variant="primary" className="spinner-border-custom" />
                    <p className="mt-3 text-muted">Cargando usuarios...</p>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fas fa-users text-muted" style={{ fontSize: '3rem' }}></i>
                    <h5 className="mt-3 text-muted">No se encontraron usuarios</h5>
                    <p className="text-muted">Intenta ajustar los filtros de búsqueda</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-custom mb-0">
                      <thead>
                        <tr>
                          <th>Usuario</th>
                          <th>Email</th>
                          <th>Rol</th>
                          <th>Estado</th>
                          <th>Fecha Registro</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user._id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="me-3">
                                  <i className="fas fa-user-circle text-primary" style={{ fontSize: '1.5rem' }}></i>
                                </div>
                                <div>
                                  <div className="fw-bold">{user.name}</div>
                                  {user.phone && (
                                    <small className="text-muted">
                                      <i className="fas fa-phone me-1"></i>
                                      {user.phone}
                                    </small>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>{user.email}</td>
                            <td>
                              <Badge 
                                bg={user.role === 'admin' ? 'danger' : 'primary'}
                                className="badge-custom"
                              >
                                <i className={`fas ${user.role === 'admin' ? 'fa-crown' : 'fa-user'} me-1`}></i>
                                {user.role === 'admin' ? 'Admin' : 'Usuario'}
                              </Badge>
                            </td>
                            <td>
                              <Badge 
                                bg={user.isActive ? 'success' : 'warning'}
                                className="badge-custom"
                              >
                                <i className={`fas ${user.isActive ? 'fa-check-circle' : 'fa-pause-circle'} me-1`}></i>
                                {user.isActive ? 'Activo' : 'Inactivo'}
                              </Badge>
                            </td>
                            <td>{formatDate(user.createdAt)}</td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => openEditModal(user)}
                                  title="Editar usuario"
                                >
                                  <i className="fas fa-edit"></i>
                                </Button>
                                
                                {user._id !== currentUser.id && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant={user.isActive ? 'outline-warning' : 'outline-success'}
                                      onClick={() => handleToggleStatus(user)}
                                      title={user.isActive ? 'Desactivar usuario' : 'Activar usuario'}
                                    >
                                      <i className={`fas ${user.isActive ? 'fa-pause' : 'fa-play'}`}></i>
                                    </Button>
                                    
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => handleDeleteClick(user)}
                                      title="Eliminar usuario"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Paginación */}
        {pagination.totalPages > 1 && (
          <Row className="mt-4">
            <Col className="d-flex justify-content-center">
              <Pagination>
                <Pagination.First 
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.currentPage === 1}
                />
                <Pagination.Prev 
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                />
                
                {[...Array(pagination.totalPages)].map((_, index) => {
                  const page = index + 1;
                  if (
                    page === 1 ||
                    page === pagination.totalPages ||
                    (page >= pagination.currentPage - 2 && page <= pagination.currentPage + 2)
                  ) {
                    return (
                      <Pagination.Item
                        key={page}
                        active={page === pagination.currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  } else if (
                    page === pagination.currentPage - 3 ||
                    page === pagination.currentPage + 3
                  ) {
                    return <Pagination.Ellipsis key={page} />;
                  }
                  return null;
                })}
                
                <Pagination.Next 
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                />
                <Pagination.Last 
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.currentPage === pagination.totalPages}
                />
              </Pagination>
            </Col>
          </Row>
        )}
      </div>

      {/* Modal para crear/editar usuario */}
      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className={`fas ${modalType === 'create' ? 'fa-plus' : 'fa-edit'} me-2`}></i>
            {modalType === 'create' ? 'Crear Nuevo Usuario' : 'Editar Usuario'}
          </Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">
                    <i className="fas fa-user me-2"></i>
                    Nombre Completo *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    className="form-control-custom"
                    required
                    minLength={2}
                    maxLength={50}
                  />
                  <Form.Control.Feedback type="invalid">
                    El nombre debe tener entre 2 y 50 caracteres.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">
                    <i className="fas fa-envelope me-2"></i>
                    Email *
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    className="form-control-custom"
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Por favor ingresa un email válido.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">
                    <i className="fas fa-lock me-2"></i>
                    Contraseña {modalType === 'create' ? '*' : '(opcional)'}
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleFormChange}
                    className="form-control-custom"
                    required={modalType === 'create'}
                    minLength={6}
                    isInvalid={validated && !validatePassword(formData.password)}
                  />
                  <Form.Control.Feedback type="invalid">
                    La contraseña debe tener al menos 6 caracteres, una mayúscula, una minúscula y un número.
                  </Form.Control.Feedback>
                  {modalType === 'edit' && (
                    <Form.Text className="text-muted">
                      Deja en blanco para mantener la contraseña actual
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">
                    <i className="fas fa-user-tag me-2"></i>
                    Rol *
                  </Form.Label>
                  <Form.Select
                    name="role"
                    value={formData.role}
                    onChange={handleFormChange}
                    className="form-control-custom"
                    required
                  >
                    <option value="">-- Selecciona un rol --</option>
                    <option value="super-admin">Super Administrador</option>
                    <option value="admin">Administrador</option>
                    <option value="secretaria">Secretaria</option>
                    <option value="socio">Socio</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    Selecciona un rol válido.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">
                    <i className="fas fa-phone me-2"></i>
                    Teléfono
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    className="form-control-custom"
                    placeholder="Ej: +57 300 123 4567"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">
                    <i className="fas fa-map-marker-alt me-2"></i>
                    Dirección
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleFormChange}
                    className="form-control-custom"
                    placeholder="Dirección completa"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              <i className="fas fa-times me-2"></i>
              Cancelar
            </Button>
            <Button type="submit" className="btn-primary-custom" disabled={loading}>
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {modalType === 'create' ? 'Creando...' : 'Actualizando...'}
                </>
              ) : (
                <>
                  <i className={`fas ${modalType === 'create' ? 'fa-plus' : 'fa-save'} me-2`}></i>
                  {modalType === 'create' ? 'Crear Usuario' : 'Actualizar Usuario'}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Confirmar Eliminación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <i className="fas fa-warning me-2"></i>
            <strong>¡Atención!</strong> Esta acción no se puede deshacer.
          </Alert>
          <p>
            ¿Estás seguro de que deseas eliminar al usuario{' '}
            <strong>{userToDelete?.name}</strong>?
          </p>
          <p className="text-muted">
            Se eliminarán todos los datos asociados a este usuario.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            <i className="fas fa-times me-2"></i>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Eliminando...
              </>
            ) : (
              <>
                <i className="fas fa-trash me-2"></i>
                Eliminar Usuario
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserManagement;